import React, { createContext, useContext, useState, ReactNode } from 'react';

interface TransitionContextType {
    isFrozen: boolean;
    showCube: boolean;
    isTransitioning: boolean;
    isZooming: boolean;
    startTransition: () => void;
    resetTransition: () => void;
    resetTransitionInstant: () => void;
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined);

export const TransitionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isFrozen, setIsFrozen] = useState(false);
    const [showCube, setShowCube] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isZooming, setIsZooming] = useState(false);

    const startTransition = React.useCallback(() => {
        setIsTransitioning(true);

        // 1. Wait 1 second for fade out / initial movement
        setTimeout(() => {
            // 2. Freeze particles
            setIsFrozen(true);

            // 3. Start zoom immediately after freezing (next tick to ensure freeze renders)
            setTimeout(() => {
                setIsZooming(true);
            }, 50);

            // 4. Show cube after zoom animation completes (1500ms duration)
            setTimeout(() => {
                setShowCube(true);
            }, 1550);

        }, 1000);
    }, []);

    const resetTransition = React.useCallback(() => {
        // 1. Hide the cube overlay immediately
        setShowCube(false);

        // 2. Start zoom-out (next tick)
        setTimeout(() => {
            setIsZooming(false);
        }, 50);

        // 3. Wait for zoom-out animation to complete, then clear all states
        setTimeout(() => {
            setIsFrozen(false);
            setIsTransitioning(false);
        }, 1550); // 1500ms zoom duration + 50ms buffer
    }, []);

    const resetTransitionInstant = React.useCallback(() => {
        setShowCube(false);
        setIsZooming(false);
        setIsFrozen(false);
        setIsTransitioning(false);
    }, []);

    const value = React.useMemo(() => ({
        isFrozen,
        showCube,
        isTransitioning,
        isZooming,
        startTransition,
        resetTransition,
        resetTransitionInstant
    }), [isFrozen, showCube, isTransitioning, isZooming, startTransition, resetTransition, resetTransitionInstant]);

    return (
        <TransitionContext.Provider value={value}>
            {children}
        </TransitionContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTransition = () => {
    const context = useContext(TransitionContext);
    if (context === undefined) {
        throw new Error('useTransition must be used within a TransitionProvider');
    }
    return context;
};
